'use client';

import { c as _c } from "react/compiler-runtime";
import type { LinkProps as NextLinkProps } from 'next/link';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { getTextFromReactNode } from '@sola/utils/react.ts';
import { isAbsoluteInternalLink, isAnchorLink, isInternalLink, isInternalPathForExternalUse } from '@sola/utils/url.ts';
import { useRouter } from '../../utils/hooks/useRouter.ts';
export type LinkProps = NextLinkProps & React.PropsWithChildren & {
  id?: string;
  className?: string;
  isDisabled?: boolean;
  title?: string;
  download?: boolean;
  type?: 'button' | 'submit' | 'reset';
  target?: string;
  rel?: string;
};

/**
 * Determines whether a given href should use full-page navigation instead of
 * Next.js client-side routing.  This is critical for the www-router legacy
 * fallback: links to paths served by the legacy site must trigger a real
 * browser navigation so the Cloudflare Worker can intercept the request and
 * route it to legacy when Skippy returns a 404.
 */
const shouldUseFullNavigation = (href: LinkProps['href']): boolean => {
  const url = typeof href === 'string' ? href : href?.pathname ?? '';
  if (!url) {
    return false;
  }
  if (url.includes('.htm')) {
    return true;
  }
  return !isInternalLink(url);
};

/**
 * Resolves a Next.js href (string or UrlObject) into a plain URL string
 * suitable for a native `<a>` tag.  Prevents `[object Object]` in the DOM.
 */
const resolveHref = (href: LinkProps['href']): string => {
  if (typeof href === 'string') {
    return href;
  }
  const {
    pathname,
    query,
    hash
  } = href ?? {};
  let url = pathname ?? '';
  if (query) {
    const params = typeof query === 'string' ? query : new URLSearchParams(query as Record<string, string>).toString();
    if (params) {
      url += `?${params}`;
    }
  }
  if (hash) {
    url += hash.startsWith('#') ? hash : `#${hash}`;
  }
  return url;
};
export const Link: React.FC<LinkProps> = t0 => {
  const $ = _c(12);
  const {
    isDisabled,
    ...props
  } = t0;
  const {
    RouterComponent
  } = useRouter();
  const handleClick = e => {
    trackClick(props.title || getTextFromReactNode(props.children) || "Unknown", resolveHref(props.href), props.className, props.id, "Link");
    if (props.onClick) {
      props.onClick(e);
    }
  };
  if (!RouterComponent || isDisabled) {
    let t1;
    if ($[0] !== isDisabled) {
      t1 = isDisabled && {
        "aria-disabled": true,
        role: "link",
        href: undefined
      };
      $[0] = isDisabled;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return <a {...props as React.ComponentProps<'a'>} {...t1} />;
  }
  if (shouldUseFullNavigation(props.href)) {
    const resolvedUrl = resolveHref(props.href);
    const isSameDomain = resolvedUrl.startsWith("/") || isAnchorLink(resolvedUrl) || isAbsoluteInternalLink(resolvedUrl) || isInternalPathForExternalUse(resolvedUrl);
    const target = props.target || (isSameDomain ? "_self" : "_blank");
    const rel = isSameDomain ? undefined : props.rel || "noopener noreferrer";
    const t1 = props as React.ComponentProps<'a'>;
    let t2;
    if ($[2] !== handleClick || $[3] !== rel || $[4] !== resolvedUrl || $[5] !== t1 || $[6] !== target) {
      t2 = <a {...t1} href={resolvedUrl} target={target} rel={rel} onClick={handleClick} />;
      $[2] = handleClick;
      $[3] = rel;
      $[4] = resolvedUrl;
      $[5] = t1;
      $[6] = target;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== RouterComponent || $[9] !== handleClick || $[10] !== props) {
    t1 = <RouterComponent {...props} onClick={handleClick} />;
    $[8] = RouterComponent;
    $[9] = handleClick;
    $[10] = props;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
};